import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { isAuthenticated, markPostLoginFeaturePromptPending, setAuth } from '../services/auth';
import { Eye, EyeOff, Landmark, Smartphone, Keyboard } from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ThemeToggle } from '../components/ThemeToggle';
import { ImageCarousel } from '../components/ImageCarousel';
import { SecurityManager } from '../components/SecurityManager';
import VirtualKeyboard from '../components/VirtualKeyboard';
import { getDefaultAuthenticatedPath } from '../config/navigation';
import { useLocalization } from '../context/LocalizationContext';
import { resolveApiErrorMessage } from '../utils/apiErrors';

const OTP_RESEND_COOLDOWN_SECONDS = 30;
const OTP_RESEND_MAX_ATTEMPTS = 3;
const INDIA_MOBILE_PATTERN = /^[6-9]\d{9}$/;
const USERNAME_MAX_LENGTH = 50;
const PASSWORD_MAX_LENGTH = 128;
const OTP_MAX_LENGTH = 6;
const SOCIETY_ID_MAX_LENGTH = 50;
const FULL_NAME_MAX_LENGTH = 100;

const sanitizeUsernameInput = (value) =>
  String(value || '')
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9@._-]/g, '')
    .slice(0, USERNAME_MAX_LENGTH);

const sanitizeSocietyIdInput = (value) =>
  String(value || '')
    .replace(/^\s+/, '')
    .slice(0, SOCIETY_ID_MAX_LENGTH);

const sanitizeFullNameInput = (value) =>
  String(value || '')
    .replace(/^\s+/, '')
    .slice(0, FULL_NAME_MAX_LENGTH);

const sanitizePasswordInput = (value) =>
  String(value || '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .slice(0, PASSWORD_MAX_LENGTH);

const sanitizeIndianMobileInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  let normalized = '';

  for (const digit of digits) {
    if (!normalized && !/[6-9]/.test(digit)) {
      continue;
    }
    if (normalized.length >= 10) {
      break;
    }
    normalized += digit;
  }

  return normalized;
};

const sanitizeOtpInput = (value) => String(value || '').replace(/\D/g, '').slice(0, OTP_MAX_LENGTH);

const maskMobileNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (INDIA_MOBILE_PATTERN.test(digits)) {
    return `${digits.slice(0, 2)}XXXXX${digits.slice(-3)}`;
  }
  if (digits.length <= 5) return `${'*'.repeat(digits.length)}`;
  if (digits.length <= 7) {
    return `${digits.slice(0, 2)}${'*'.repeat(digits.length - 2)}`;
  }
  return `${digits.slice(0, 2)}${'*'.repeat(Math.max(0, digits.length - 5))}${digits.slice(-3)}`;
};

const assertSuccessfulRestResponse = (response, fallbackMessage) => {
  const data = typeof response?.data === 'string' ? JSON.parse(response.data) : response?.data;
  if (String(data?.status || '').toUpperCase() === 'FAILED' || data?.success === false) {
    throw new Error(data?.errorDescription || data?.message || fallbackMessage);
  }
  return data;
};

const normalizeAuthPayload = (payload, fallback = {}) => {
  const root = payload?.value && typeof payload.value === 'object' ? payload.value : payload || {};
  const nestedUser = root.user && typeof root.user === 'object' ? root.user : {};
  const name =
    root.memberName ||
    root.customerName ||
    root.agentName ||
    root.name ||
    nestedUser.memberName ||
    nestedUser.customerName ||
    nestedUser.name ||
    fallback.name ||
    fallback.username ||
    'Member';

  return {
    token: root.token || payload?.token,
    customerId: root.customerId || nestedUser.customerId || fallback.customerId || '',
    agentName: name,
    tenant: root.tenant || payload?.tenant || null,
    mobileNumber: root.mobileNumber || nestedUser.mobileNumber || fallback.mobileNumber || '',
    memberGl: root.memberGl || root.glId || nestedUser.memberGl || nestedUser.glId || fallback.memberGl || '',
  };
};

export const Login = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { t, language, tenantBrand } = useLocalization();
  const defaultAuthenticatedPath = getDefaultAuthenticatedPath();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [loginMode, setLoginMode] = useState('password');
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  const [otpResendCount, setOtpResendCount] = useState(0);
  const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const [signupStep, setSignupStep] = useState('details'); // 'details', 'otp', 'password'
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const societyIdInputRef = useRef(null);
  const fullNameInputRef = useRef(null);
  const confirmPasswordInputRef = useRef(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    mobileNumber: '',
    otp: '',
    societyId: '',
    fullName: '',
    confirmPassword: '',
  });
  const maskedMobileNumber = maskMobileNumber(formData.mobileNumber);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(defaultAuthenticatedPath, { replace: true });
    }
  }, [defaultAuthenticatedPath, navigate]);

  useEffect(() => {
    if (!otpSent || otpResendCountdown <= 0) return undefined;
    const timerId = window.setInterval(() => {
      setOtpResendCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [otpSent, otpResendCountdown]);

  const handleVirtualKeyboardInput = useCallback((value) => {
    if (!activeInput) return;

    const inputName = activeInput;

    if (value === 'BACKSPACE') {
      setFormData(prev => ({
        ...prev,
        [inputName]: prev[inputName].slice(0, -1)
      }));
    } else if (value === 'ENTER') {
      // Handle enter key based on current context
      if (authMode === 'login') {
        if (loginMode === 'password') {
          handlePasswordLogin(new Event('submit'));
        } else if (loginMode === 'otp' && otpSent) {
          handleOTPLogin(new Event('submit'));
        } else if (loginMode === 'otp' && !otpSent) {
          handleRequestOTP(new Event('submit'));
        }
      } else if (authMode === 'signup') {
        if (signupStep === 'details') {
          handleRequestSignupOTP(new Event('submit'));
        } else if (signupStep === 'otp') {
          handleVerifySignupOTP(new Event('submit'));
        } else if (signupStep === 'password') {
          handleCompleteRegistration(new Event('submit'));
        }
      }
    } else if (value === 'CLEAR') {
      setFormData(prev => ({
        ...prev,
        [inputName]: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [inputName]: prev[inputName] + value
      }));
    }
  }, [activeInput, loginMode, otpSent, authMode, signupStep]);

  const handleInputFocus = useCallback((inputName) => {
    setActiveInput(inputName);
  }, []);

  const handleVirtualKeyboardOpen = useCallback((inputName, inputRef) => {
    setActiveInput(inputName);
    setShowVirtualKeyboard(true);
    inputRef?.current?.focus();
  }, []);

  const handleVirtualKeyboardClose = useCallback(() => {
    setShowVirtualKeyboard(false);
    setActiveInput(null);
  }, []);

  const getActiveInputElement = useCallback(() => {
    switch (activeInput) {
      case 'username': return usernameInputRef.current;
      case 'password': return passwordInputRef.current;
      case 'mobileNumber': return mobileInputRef.current;
      case 'otp': return otpInputRef.current;
      case 'societyId': return societyIdInputRef.current;
      case 'fullName': return fullNameInputRef.current;
      case 'confirmPassword': return confirmPasswordInputRef.current;
      default: return null;
    }
  }, [activeInput]);

  const resetOtpFlow = () => {
    setOtpSent(false);
    setOtpResendCountdown(0);
    setOtpResendCount(0);
    setFormData((prev) => ({ ...prev, otp: '' }));
  };

  const resetSignupFlow = () => {
    setSignupStep('details');
    setOtpSent(false);
    setOtpResendCountdown(0);
    setOtpResendCount(0);
    setFormData((prev) => ({
      ...prev,
      societyId: '',
      fullName: '',
      mobileNumber: '',
      otp: '',
      password: '',
      confirmPassword: '',
    }));
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    const username = sanitizeUsernameInput(formData.username);
    const password = sanitizePasswordInput(formData.password);

    if (!username || !password.trim()) {
      toast.error(t('login.pleaseEnterUsernamePassword'));
      return;
    }
    setLoading(true);

    try {
      const response = await authAPI.login({
        username,
        password,
      });

      const { token, customerId, agentName, tenant, mobileNumber, memberGl } = normalizeAuthPayload(
        response.data,
        {
          username,
        }
      );
      if (!token) {
        throw new Error(t('login.tokenMissing'));
      }

      const user = {
        customerId,
        name: agentName,
        username,
        mobileNumber,
        memberGl,
      };

      setAuth(token, user, tenant);
      markPostLoginFeaturePromptPending();
      toast.success(t('login.welcomeMessage'));
      navigate(defaultAuthenticatedPath);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.loginFailed')));
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async ({ isResend = false } = {}) => {
    const mobileNumber = sanitizeIndianMobileInput(formData.mobileNumber);

    if (!INDIA_MOBILE_PATTERN.test(mobileNumber)) {
      toast.error(t('login.enterValidMobile'));
      return;
    }

    setLoading(true);
    try {
      await authAPI.requestOTP(mobileNumber);
      setOtpSent(true);
      setOtpResendCountdown(OTP_RESEND_COOLDOWN_SECONDS);
      setFormData((prev) => ({ ...prev, mobileNumber }));
      if (isResend) {
        setOtpResendCount((prev) => prev + 1);
        setFormData((prev) => ({ ...prev, otp: '' }));
        toast.success(t('login.otpResent'));
      } else {
        setOtpResendCount(0);
        toast.success(t('login.otpSent'));
      }
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.otpSendFailed')));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e?.preventDefault();
    await sendOtp({ isResend: false });
  };

  const handleResendOTP = async () => {
    if (otpResendCountdown > 0 || otpResendCount >= OTP_RESEND_MAX_ATTEMPTS) {
      return;
    }
    if (authMode === 'signup' && signupStep === 'otp') {
      await requestSignupOtp({ isResend: true });
      return;
    }
    await sendOtp({ isResend: true });
  };

  const handleOTPLogin = async (e) => {
    e.preventDefault();
    if (!otpSent) {
      await handleRequestOTP(e);
      return;
    }
    const mobileNumber = sanitizeIndianMobileInput(formData.mobileNumber);
    const otp = sanitizeOtpInput(formData.otp);

    if (otp.length !== OTP_MAX_LENGTH) {
      toast.error(t('login.enterValidOtp'));
      return;
    }
    setLoading(true);

    try {
      const response = await authAPI.verifyOTP({
        mobileNumber,
        otp,
        username: '',
      });

      const { token, customerId, agentName, tenant, mobileNumber: resolvedMobileNumber, memberGl } =
        normalizeAuthPayload(response.data, {
          mobileNumber,
        });
      if (!token) {
        throw new Error(t('login.tokenMissing'));
      }

      const user = {
        customerId,
        name: agentName,
        mobileNumber: resolvedMobileNumber || mobileNumber,
        memberGl,
      };

      setAuth(token, user, tenant);
      markPostLoginFeaturePromptPending();
      toast.success(t('login.welcomeMessage'));
      navigate(defaultAuthenticatedPath);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.otpFailed')));
    } finally {
      setLoading(false);
    }
  };

  const requestSignupOtp = async ({ isResend = false } = {}) => {
    const societyId = sanitizeSocietyIdInput(formData.societyId).trim();
    const fullName = sanitizeFullNameInput(formData.fullName).trim();
    const mobileNumber = sanitizeIndianMobileInput(formData.mobileNumber);

    if (!societyId || !fullName.trim() || !INDIA_MOBILE_PATTERN.test(mobileNumber)) {
      toast.error(t('login.pleaseEnterSocietyIdNameMobile'));
      return;
    }

    setLoading(true);
    try {
      const verifyResponse = await authAPI.verifySignupMember({
        societyId,
        userName: fullName,
        mobileNumber,
      });
      assertSuccessfulRestResponse(verifyResponse, t('login.memberVerificationFailed', 'Member verification failed'));

      const response = await authAPI.requestSignupOTP({
        societyId,
        fullName,
        mobileNumber,
      });
      assertSuccessfulRestResponse(response, t('login.signupOtpSendFailed'));
      setOtpSent(true);
      setOtpResendCountdown(OTP_RESEND_COOLDOWN_SECONDS);
      setFormData((prev) => ({ ...prev, societyId, fullName, mobileNumber }));
      setSignupStep('otp');
      if (isResend) {
        setOtpResendCount((prev) => prev + 1);
        setFormData((prev) => ({ ...prev, otp: '' }));
        toast.success(t('login.otpResent'));
      } else {
        setOtpResendCount(0);
        toast.success(t('login.signupOtpSent'));
      }
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.signupOtpSendFailed')));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSignupOTP = async (e) => {
    e?.preventDefault();
    await requestSignupOtp({ isResend: false });
  };

  const handleVerifySignupOTP = async (e) => {
    e?.preventDefault();
    const mobileNumber = sanitizeIndianMobileInput(formData.mobileNumber);
    const otp = sanitizeOtpInput(formData.otp);

    if (otp.length !== OTP_MAX_LENGTH) {
      toast.error(t('login.enterValidOtp'));
      return;
    }
    setLoading(true);

    try {
      const response = await authAPI.verifySignupOTP({
        societyId: formData.societyId,
        mobileNumber,
        otp,
      });
      assertSuccessfulRestResponse(response, t('login.signupOtpFailed'));
      setSignupStep('password');
      toast.success(t('login.verifySignupOtp'));
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.signupOtpFailed')));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e?.preventDefault();
    const password = sanitizePasswordInput(formData.password);
    const confirmPassword = sanitizePasswordInput(formData.confirmPassword);

    if (!password || !confirmPassword) {
      toast.error(t('login.enterPassword'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('login.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.completeRegistration({
        societyId: formData.societyId.trim(),
        userName: formData.fullName.trim(),
        mobileNumber: formData.mobileNumber,
        password,
        confirmPassword,
      });

      const { token, customerId, agentName, tenant, mobileNumber: resolvedMobileNumber, memberGl } =
        normalizeAuthPayload(response.data, {
          mobileNumber: formData.mobileNumber,
          memberGl: formData.societyId.trim(),
          name: formData.fullName.trim(),
        });
      if (!token) {
        toast.success(t('login.registrationSuccess'));
        setAuthMode('login');
        setSignupStep('details');
        setOtpSent(false);
        setOtpResendCountdown(0);
        setOtpResendCount(0);
        setFormData((prev) => ({
          ...prev,
          username: formData.societyId,
          password: '',
          otp: '',
          confirmPassword: '',
        }));
        return;
      }

      const user = {
        customerId,
        name: agentName,
        mobileNumber: resolvedMobileNumber || formData.mobileNumber,
        memberGl,
      };

      setAuth(token, user, tenant);
      markPostLoginFeaturePromptPending();
      toast.success(t('login.registrationSuccess'));
      navigate(defaultAuthenticatedPath);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.registrationFailed')));
    } finally {
      setLoading(false);
    }
  };

  const handleAuthModeSwitch = (mode) => {
    setAuthMode(mode);
    if (mode === 'login') {
      resetOtpFlow();
    } else {
      resetSignupFlow();
    }
  };

  return (
    <SecurityManager enabled={true}>
      <div className="login-page-shell h-[100dvh] max-h-[100dvh] overflow-hidden grid md:grid-cols-2 app-page-shell-bg transition-colors relative">

        {/* LEFT HALF - BANNERS */}
        <div className="hidden md:flex relative flex-col h-full min-h-0 bg-gradient-to-br from-primary-200/70 via-primary-50/80 to-primary-50/90 dark:from-slate-900 dark:to-slate-950">
          <ImageCarousel
            imageFolder="/banners/"
            autoPlay={true}
            interval={5000}
            showIndicators={true}
            showArrows={false}
            className="w-full h-full"
          />
        </div>

        {/* RIGHT HALF - LOGIN FORM & INFO */}
        <div className="flex flex-col h-full min-h-0">
          {/* TOP - CONTROLS */}
          <div className="flex-shrink-0 pt-2 pb-1 px-4 sm:px-6 md:px-8 flex justify-end">
            <div className="flex gap-2">
              <LanguageSwitcher compact />
              <ThemeToggle compact />
            </div>
          </div>

          {/* CENTER - LOGIN FORM */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-8 md:px-8 py-1">
            <div className="w-full max-w-md mx-auto min-h-full flex flex-col justify-center space-y-3 sm:space-y-4 slide-up">
              {/* LOGO, TENANT NAME & APP NAME */}
              <div className="text-center space-y-2 sm:space-y-3">
                {/* Logo - Client logo with fallback */}
                <div className="flex justify-center">
                  <img
                    src={`${process.env.PUBLIC_URL}/login.logo.png`}
                    alt={tenantBrand.fullName}
                    style={{
                      width: tenantBrand.logoSize?.login?.width || '96px',
                      height: tenantBrand.logoSize?.login?.height || '96px',
                    }}
                    className="object-contain drop-shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = '<div class="bg-primary-500 p-5 rounded-3xl shadow-xl"><svg class="w-14 h-14 sm:w-16 sm:h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg></div>';
                    }}
                  />
                </div>

                {/* Tenant Name - Full width, no line breaks forced */}
                <p className="text-primary-800 dark:text-primary-200 font-bold text-base sm:text-lg leading-snug px-2">
                  {tenantBrand.fullName}
                </p>

                {/* App Name */}
                <p className="text-primary-600 dark:text-primary-400 font-semibold text-base sm:text-lg tracking-wide">
                  {t('login.selfServicePortal')}
                </p>
              </div>

              <div className="rounded-[1.75rem] sm:rounded-[2rem] border border-primary-100/90 bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-[0_24px_60px_-28px_rgba(240,99,33,0.28)] dark:border-slate-800 dark:bg-slate-900">
                {authMode === 'login' && (
                  <>
                    {/* LOGIN TABS */}
                    <div className="flex gap-2 bg-primary-50 dark:bg-slate-800 p-1 rounded-xl">
                      <button
                        onClick={() => {
                          setLoginMode('password');
                          resetOtpFlow();
                        }}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${loginMode === 'password'
                          ? 'bg-white dark:bg-slate-900 text-primary-900 dark:text-white shadow-sm'
                          : 'text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white'
                          }`}
                        data-testid="password-login-tab"
                      >
                        {t('login.password')}
                      </button>
                      <button
                        onClick={() => {
                          setLoginMode('otp');
                          resetOtpFlow();
                          setFormData((prev) => ({ ...prev, password: '' }));
                        }}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${loginMode === 'otp'
                          ? 'bg-white dark:bg-slate-900 text-primary-900 dark:text-white shadow-sm'
                          : 'text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white'
                          }`}
                        data-testid="otp-login-tab"
                      >
                        <Smartphone size={16} />
                        {t('login.otp')}
                      </button>
                    </div>

                    {/* LOGIN FORMS */}
                    {loginMode === 'password' ? (
                      <form onSubmit={handlePasswordLogin} className="space-y-4 pt-4">
                        <div className="space-y-4" data-testid="login-form">
                          <div className="space-y-1.5">
                            <label htmlFor="username" className="block text-sm font-medium text-primary-900 dark:text-white">
                              {t('login.usernameOrCustomerId')}
                            </label>
                            <div className="relative">
                              <input
                                ref={usernameInputRef}
                                id="username"
                                data-testid="username-input"
                                type="text"
                                required
                                maxLength={USERNAME_MAX_LENGTH}
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: sanitizeUsernameInput(e.target.value) })}
                                onFocus={() => handleInputFocus('username')}
                                className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none transition-all"
                                placeholder={t('login.enterUsername')}
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                readOnly={showVirtualKeyboard && activeInput === 'username'}
                              />
                              <button
                                type="button"
                                onClick={() => handleVirtualKeyboardOpen('username', usernameInputRef)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                                aria-label="Toggle Virtual Keyboard"
                              >
                                <Keyboard size={20} />
                              </button>
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <label htmlFor="password" className="block text-sm font-medium text-primary-900 dark:text-white">
                              {t('login.password')}
                            </label>
                            <div className="relative">
                              <input
                                ref={passwordInputRef}
                                id="password"
                                data-testid="password-input"
                                type={showPassword ? 'text' : 'password'}
                                required
                                maxLength={PASSWORD_MAX_LENGTH}
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: sanitizePasswordInput(e.target.value) })}
                                onFocus={() => handleInputFocus('password')}
                                className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-24 outline-none transition-all"
                                placeholder={t('login.enterPassword')}
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                readOnly={showVirtualKeyboard && activeInput === 'password'}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-12 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                                data-testid="toggle-password-visibility"
                                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                              >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleVirtualKeyboardOpen('password', passwordInputRef)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                                aria-label="Toggle Virtual Keyboard"
                              >
                                <Keyboard size={20} />
                              </button>
                            </div>
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          data-testid="login-submit-button"
                          className="app-primary-button w-full"
                        >
                          {loading ? t('login.signingIn') : t('login.signIn')}
                        </button>

                        <div className="pt-4 text-center">
                          <p className="text-sm text-primary-600 dark:text-primary-300">
                            {t('login.newUser')}{' '}
                            <button
                              type="button"
                              onClick={() => handleAuthModeSwitch('signup')}
                              className="font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 underline"
                              data-testid="signup-link"
                            >
                              {t('login.registerNow')}
                            </button>
                          </p>
                        </div>
                      </form>
                    ) : (
                      <>
                        <form onSubmit={handleOTPLogin} className="space-y-4 pt-4">
                          <div className="space-y-1.5">
                            <label htmlFor="mobileNumber" className="block text-sm font-medium text-primary-900 dark:text-white">
                              {t('login.mobileNumber')}
                            </label>
                            <div className="relative">
                              <input
                                ref={mobileInputRef}
                                id="mobileNumber"
                                data-testid="mobile-input"
                                type="tel"
                                required
                                maxLength={10}
                                value={formData.mobileNumber}
                                onChange={(e) =>
                                  setFormData({ ...formData, mobileNumber: sanitizeIndianMobileInput(e.target.value) })
                                }
                                onFocus={() => handleInputFocus('mobileNumber')}
                                className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none transition-all"
                                placeholder={t('login.enterMobileNumber')}
                                disabled={otpSent}
                                inputMode="numeric"
                                readOnly={showVirtualKeyboard && activeInput === 'mobileNumber'}
                              />
                              <button
                                type="button"
                                onClick={() => handleVirtualKeyboardOpen('mobileNumber', mobileInputRef)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                                aria-label="Toggle Virtual Keyboard"
                                disabled={otpSent}
                              >
                                <Keyboard size={20} />
                              </button>
                            </div>
                            {!otpSent && (
                              <p className="flex items-start gap-1.5 text-xs sm:text-sm text-primary-600 dark:text-primary-300 leading-relaxed">
                                <span aria-hidden="true" className="text-sm leading-none mt-0.5">🇮🇳</span>
                                <span>{t('login.mobileNumberHint')}</span>
                              </p>
                            )}
                          </div>

                          {!otpSent ? (
                            <button
                              type="submit"
                              disabled={loading}
                              data-testid="request-otp-button"
                              className="app-primary-button w-full"
                            >
                              {loading ? t('login.sendingOtp') : t('login.sendOtp')}
                            </button>
                          ) : (
                            <div className="space-y-4">
                              <div className="space-y-1.5">
                                <label htmlFor="otp" className="block text-sm font-medium text-primary-900 dark:text-white">
                                  {t('login.enterOtp')}
                                </label>
                                <p className="text-xs sm:text-sm text-primary-600 dark:text-primary-300">
                                  {t('login.otpSentTo', { mobileNumber: maskedMobileNumber })}
                                </p>
                                <div className="relative">
                                  <input
                                    ref={otpInputRef}
                                    id="otp"
                                    data-testid="otp-input"
                                    type="text"
                                    required
                                    maxLength={OTP_MAX_LENGTH}
                                    value={formData.otp}
                                    onChange={(e) => setFormData({ ...formData, otp: sanitizeOtpInput(e.target.value) })}
                                    onFocus={() => handleInputFocus('otp')}
                                    className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none text-center text-2xl tracking-widest transition-all"
                                    placeholder="000000"
                                    inputMode="numeric"
                                    readOnly={showVirtualKeyboard && activeInput === 'otp'}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleVirtualKeyboardOpen('otp', otpInputRef)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                                    aria-label="Toggle Virtual Keyboard"
                                  >
                                    <Keyboard size={20} />
                                  </button>
                                </div>
                              </div>

                              <div className="flex flex-col gap-3 sm:flex-row">
                                <button
                                  type="submit"
                                  disabled={loading}
                                  data-testid="verify-otp-button"
                                  className="app-primary-button w-full"
                                >
                                  {loading ? t('login.verifyingOtp') : t('login.verifyAndLogin')}
                                </button>
                                <button
                                  type="button"
                                  onClick={handleResendOTP}
                                  disabled={loading || otpResendCountdown > 0 || otpResendCount >= OTP_RESEND_MAX_ATTEMPTS}
                                  className="w-full sm:w-auto whitespace-nowrap rounded-full border border-primary-200 px-5 py-3 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-primary-100 disabled:text-primary-300 dark:border-primary-500/40 dark:text-primary-300 dark:hover:bg-primary-900/20 dark:disabled:border-slate-700 dark:disabled:text-slate-500"
                                  data-testid="resend-otp-button"
                                >
                                  {otpResendCountdown > 0
                                    ? t('login.resendOtpIn', { seconds: otpResendCountdown })
                                    : t('login.resendOtp')}
                                </button>
                              </div>

                              <div className="flex flex-col items-start gap-2 text-sm">
                                <button
                                  type="button"
                                  onClick={resetOtpFlow}
                                  className="text-primary-500 hover:text-primary-600 hover:underline"
                                  data-testid="change-number-button"
                                >
                                  {t('login.changeMobileNumber')}
                                </button>
                                {otpResendCount >= OTP_RESEND_MAX_ATTEMPTS && (
                                  <p className="text-primary-600 dark:text-primary-300">
                                    {t('login.resendOtpLimitReached')}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </form>
                      </>
                    )}
                  </>
                )}

                {authMode === 'signup' && (
                  <>
                    {/* SIGNUP FORMS */}
                    {signupStep === 'details' && (
                      <form onSubmit={handleRequestSignupOTP} className="space-y-4 pt-4">
                        <div className="space-y-1.5">
                          <label htmlFor="societyId" className="block text-sm font-medium text-primary-900 dark:text-white">
                            {t('login.societyId')}
                          </label>
                          <div className="relative">
                            <input
                              ref={societyIdInputRef}
                              id="societyId"
                              data-testid="society-id-input"
                              type="text"
                              required
                              maxLength={SOCIETY_ID_MAX_LENGTH}
                              value={formData.societyId}
                              onChange={(e) => setFormData({ ...formData, societyId: sanitizeSocietyIdInput(e.target.value) })}
                              onFocus={() => handleInputFocus('societyId')}
                              className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none transition-all"
                              placeholder={t('login.enterSocietyId')}
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              readOnly={showVirtualKeyboard && activeInput === 'societyId'}
                            />
                            <button
                              type="button"
                              onClick={() => handleVirtualKeyboardOpen('societyId', societyIdInputRef)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              aria-label="Toggle Virtual Keyboard"
                            >
                              <Keyboard size={20} />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="fullName" className="block text-sm font-medium text-primary-900 dark:text-white">
                            {t('login.fullName')}
                          </label>
                          <div className="relative">
                            <input
                              ref={fullNameInputRef}
                              id="fullName"
                              data-testid="full-name-input"
                              type="text"
                              required
                              maxLength={FULL_NAME_MAX_LENGTH}
                              value={formData.fullName}
                              onChange={(e) => setFormData({ ...formData, fullName: sanitizeFullNameInput(e.target.value) })}
                              onFocus={() => handleInputFocus('fullName')}
                              className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none transition-all"
                              placeholder={t('login.enterFullName')}
                              autoCapitalize="words"
                              autoCorrect="off"
                              spellCheck={false}
                              readOnly={showVirtualKeyboard && activeInput === 'fullName'}
                            />
                            <button
                              type="button"
                              onClick={() => handleVirtualKeyboardOpen('fullName', fullNameInputRef)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              aria-label="Toggle Virtual Keyboard"
                            >
                              <Keyboard size={20} />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="signupMobileNumber" className="block text-sm font-medium text-primary-900 dark:text-white">
                            {t('login.mobileNumber')}
                          </label>
                          <div className="relative">
                            <input
                              ref={mobileInputRef}
                              id="signupMobileNumber"
                              data-testid="signup-mobile-input"
                              type="tel"
                              required
                              maxLength={10}
                              value={formData.mobileNumber}
                              onChange={(e) =>
                                setFormData({ ...formData, mobileNumber: sanitizeIndianMobileInput(e.target.value) })
                              }
                              onFocus={() => handleInputFocus('mobileNumber')}
                              className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none transition-all"
                              placeholder={t('login.enterMobileNumber')}
                              inputMode="numeric"
                              readOnly={showVirtualKeyboard && activeInput === 'mobileNumber'}
                            />
                            <button
                              type="button"
                              onClick={() => handleVirtualKeyboardOpen('mobileNumber', mobileInputRef)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              aria-label="Toggle Virtual Keyboard"
                            >
                              <Keyboard size={20} />
                            </button>
                          </div>
                          <p className="flex items-start gap-1.5 text-xs sm:text-sm text-primary-600 dark:text-primary-300 leading-relaxed">
                            <span aria-hidden="true" className="text-sm leading-none mt-0.5">🇮🇳</span>
                            <span>{t('login.mobileNumberHint')}</span>
                          </p>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          data-testid="request-signup-otp-button"
                          className="app-primary-button w-full"
                        >
                          {loading ? t('login.requestingSignupOtp') : t('login.requestSignupOtp')}
                        </button>

                        <div className="pt-4 text-center">
                          <p className="text-sm text-primary-600 dark:text-primary-300">
                            {t('login.alreadyHaveAccount')}{' '}
                            <button
                              type="button"
                              onClick={() => handleAuthModeSwitch('login')}
                              className="font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 underline"
                              data-testid="login-link"
                            >
                              {t('login.signIn')}
                            </button>
                          </p>
                        </div>
                      </form>
                    )}

                    {signupStep === 'otp' && (
                      <form onSubmit={handleVerifySignupOTP} className="space-y-4 pt-4">
                        <div className="space-y-1.5">
                          <label htmlFor="signupOtp" className="block text-sm font-medium text-primary-900 dark:text-white">
                            {t('login.enterOtp')}
                          </label>
                          <p className="text-xs sm:text-sm text-primary-600 dark:text-primary-300">
                            {t('login.otpSentTo', { mobileNumber: maskedMobileNumber })}
                          </p>
                          <div className="relative">
                            <input
                              ref={otpInputRef}
                              id="signupOtp"
                              data-testid="signup-otp-input"
                              type="text"
                              required
                              maxLength={OTP_MAX_LENGTH}
                              value={formData.otp}
                              onChange={(e) => setFormData({ ...formData, otp: sanitizeOtpInput(e.target.value) })}
                              onFocus={() => handleInputFocus('otp')}
                              className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none text-center text-2xl tracking-widest transition-all"
                              placeholder="000000"
                              inputMode="numeric"
                              readOnly={showVirtualKeyboard && activeInput === 'otp'}
                            />
                            <button
                              type="button"
                              onClick={() => handleVirtualKeyboardOpen('otp', otpInputRef)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              aria-label="Toggle Virtual Keyboard"
                            >
                              <Keyboard size={20} />
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row">
                          <button
                            type="submit"
                            disabled={loading}
                            data-testid="verify-signup-otp-button"
                            className="app-primary-button w-full"
                          >
                            {loading ? t('login.verifyingSignupOtp') : t('login.verifySignupOtp')}
                          </button>
                          <button
                            type="button"
                            onClick={handleResendOTP}
                            disabled={loading || otpResendCountdown > 0 || otpResendCount >= OTP_RESEND_MAX_ATTEMPTS}
                            className="w-full sm:w-auto whitespace-nowrap rounded-full border border-primary-200 px-5 py-3 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:bg-primary-100 disabled:text-primary-300 dark:border-primary-500/40 dark:text-primary-300 dark:hover:bg-primary-900/20 dark:disabled:border-slate-700 dark:disabled:text-slate-500"
                            data-testid="resend-signup-otp-button"
                          >
                            {otpResendCountdown > 0
                              ? t('login.resendOtpIn', { seconds: otpResendCountdown })
                              : t('login.resendOtp')}
                          </button>
                        </div>

                        <div className="flex flex-col items-start gap-2 text-sm">
                          <button
                            type="button"
                            onClick={() => setSignupStep('details')}
                            className="text-primary-500 hover:text-primary-600 hover:underline"
                            data-testid="change-signup-details-button"
                          >
                            {t('login.changeMobileNumber')}
                          </button>
                          {otpResendCount >= OTP_RESEND_MAX_ATTEMPTS && (
                            <p className="text-primary-600 dark:text-primary-300">
                              {t('login.resendOtpLimitReached')}
                            </p>
                          )}
                        </div>
                      </form>
                    )}

                    {signupStep === 'password' && (
                      <form onSubmit={handleCompleteRegistration} className="space-y-4 pt-4">
                        <div className="space-y-1.5">
                          <label htmlFor="signupPassword" className="block text-sm font-medium text-primary-900 dark:text-white">
                            {t('login.setPassword')}
                          </label>
                          <div className="relative">
                            <input
                              ref={passwordInputRef}
                              id="signupPassword"
                              data-testid="signup-password-input"
                              type={showPassword ? 'text' : 'password'}
                              required
                              maxLength={PASSWORD_MAX_LENGTH}
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: sanitizePasswordInput(e.target.value) })}
                              onFocus={() => handleInputFocus('password')}
                              className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-24 outline-none transition-all"
                              placeholder={t('login.enterPassword')}
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              readOnly={showVirtualKeyboard && activeInput === 'password'}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-12 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              data-testid="toggle-signup-password-visibility"
                              aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                            >
                              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleVirtualKeyboardOpen('password', passwordInputRef)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              aria-label="Toggle Virtual Keyboard"
                            >
                              <Keyboard size={20} />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-primary-900 dark:text-white">
                            {t('login.confirmPassword')}
                          </label>
                          <div className="relative">
                            <input
                              ref={confirmPasswordInputRef}
                              id="confirmPassword"
                              data-testid="confirm-password-input"
                              type={showConfirmPassword ? 'text' : 'password'}
                              required
                              maxLength={PASSWORD_MAX_LENGTH}
                              value={formData.confirmPassword}
                              onChange={(e) => setFormData({ ...formData, confirmPassword: sanitizePasswordInput(e.target.value) })}
                              onFocus={() => handleInputFocus('confirmPassword')}
                              className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-24 outline-none transition-all"
                              placeholder={t('login.enterConfirmPassword')}
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              readOnly={showVirtualKeyboard && activeInput === 'confirmPassword'}
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-12 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              data-testid="toggle-confirm-password-visibility"
                              aria-label={showConfirmPassword ? t('login.hidePassword') : t('login.showPassword')}
                            >
                              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleVirtualKeyboardOpen('confirmPassword', confirmPasswordInputRef)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                              aria-label="Toggle Virtual Keyboard"
                            >
                              <Keyboard size={20} />
                            </button>
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          data-testid="complete-registration-button"
                          className="app-primary-button w-full"
                        >
                          {loading ? t('login.completingRegistration') : t('login.completeRegistration')}
                        </button>

                        <div className="pt-4 text-center">
                          <p className="text-sm text-primary-600 dark:text-primary-300">
                            {t('login.alreadyHaveAccount')}{' '}
                            <button
                              type="button"
                              onClick={() => handleAuthModeSwitch('login')}
                              className="font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 underline"
                              data-testid="login-link"
                            >
                              {t('login.signIn')}
                            </button>
                          </p>
                        </div>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* BOTTOM - FOOTER */}
          <div className="flex-shrink-0 border-t border-primary-200 dark:border-slate-800 px-5 sm:px-8 md:px-8 py-3 sm:py-4 space-y-1">
            {/* <p className="text-center text-sm text-primary-600 dark:text-primary-300 leading-relaxed">
            {t('login.secureServices')}
          </p> */}
            <p className="text-center text-xs text-primary-600 dark:text-primary-300">
              © {currentYear} {t('login.copyright')}
            </p>
            <p className="text-center text-xs text-primary-600 dark:text-primary-300">
              {t('login.poweredBy', {}, 'Powered by')}{' '}
              <a
                href="https://www.ykinnosoft.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary-600 underline decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-900 hover:decoration-primary-500 dark:text-primary-300 dark:decoration-primary-500/80 dark:hover:text-white dark:hover:decoration-white"
              >
                {t('layout.companyName', {}, 'YK Innosoft Technologies')}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Virtual Keyboard */}
      {showVirtualKeyboard && (
        <VirtualKeyboard
          onInput={handleVirtualKeyboardInput}
          onClose={handleVirtualKeyboardClose}
          targetInput={getActiveInputElement()}
          showNumbers={true}
          showSymbols={true}
        />
      )}
    </SecurityManager>
  );
};
